import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import NextLink from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {NavigationDocumentData} from 'prismicio-types'
import {Fragment, useCallback, useState} from 'react'
import {CookieConsentPopup} from 'src/common/Layout/Footer/CookieConsentPopup'
import {Link} from 'src/common/Link'

/**
 * Props for `FooterNoNav`.
 */
export type FooterNoNavProps = {
  navigationData: NavigationDocumentData
}

/**
 * Component for "FooterNoNav" Slices.
 */
export const FooterNoNavDefault = ({navigationData}: FooterNoNavProps): JSX.Element => {
  const [isPopupShown, setIsPopupShown] = useState(false)
  const close = useCallback(() => setIsPopupShown(false), [])
  const {linkResolver} = useLinkResolver()

  const showCookieConsent = useCallback(() => {
    if (typeof window !== 'undefined') {
      // @ts-ignore -- usercentrics script is loaded by GTM
      window.UC_UI?.showSecondLayer()
    }
  }, [])

  return (
    <footer className="grid grid-cols-main bg-2a2928 pb-20 pt-10 text-ffffff">
      <div className="col-[inner] grid gap-y-6 md:col-end-[track-start_9] md:row-start-5 lg:col-end-[track-end_8]">
        <div className="grid items-center justify-start gap-4 sm:grid-flow-col">
          {navigationData.underline_links.map((link, index) => (
            <Fragment key={index}>
              <Link
                className="text-14"
                href={linkResolver(link.link)}
                {...(isFilled.link(link.link) && link.link.link_type === 'Web' && {target: link.link.target})}
                isInverted
              >
                {link.label}
              </Link>

              <hr className="hidden h-3 border-l border-t-0 border-l-ffffff sm:block" />
            </Fragment>
          ))}

          <button className="text-start text-14" onClick={showCookieConsent} type="button">
            {navigationData.cookie_consent_label}
          </button>
        </div>

        <div className="text-10 text-b8b6b4">
          <PrismicRichText field={navigationData.description} />
        </div>

        <div className="grid grid-flow-col justify-start gap-x-6 text-10 text-b8b6b4">
          <span>{navigationData.copyright}</span>
          <span>{navigationData.additional_text}</span>
        </div>
      </div>

      {isFilled.image(navigationData.image) && (
        <NextLink
          aria-label={navigationData.image.alt || undefined}
          className="col-start-[track-start_5] col-end-[track-end_6] row-start-5 mt-7.5 justify-self-end md:col-start-[track-start_12] md:mt-0 md:self-end"
          href={linkResolver(navigationData.image_link)}
          {...(isFilled.link(navigationData.image_link) &&
            navigationData.image_link.link_type === 'Web' && {target: navigationData.image_link.target})}
        >
          <PrismicNextImage field={navigationData.image} />
        </NextLink>
      )}

      <CookieConsentPopup isShown={isPopupShown} navigationData={navigationData} close={close} />
    </footer>
  )
}
